import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const DishDetailModal = ({ dish, isOpen, onClose, onEdit }) => {
  if (!isOpen || !dish) return null;

  const getDietaryIcon = (restriction) => {
    const icons = {
      vegetarian: 'Leaf',
      vegan: 'Sprout',
      'gluten-free': 'Wheat',
      'dairy-free': 'Milk',
      'nut-free': 'Nut',
      halal: 'Moon',
      kosher: 'Star'
    };
    return icons?.[restriction] || 'AlertCircle';
  };

  const getCuisineColor = (cuisine) => {
    const colors = {
      italian: 'bg-red-100 text-red-800',
      french: 'bg-blue-100 text-blue-800',
      asian: 'bg-yellow-100 text-yellow-800',
      mexican: 'bg-orange-100 text-orange-800',
      indian: 'bg-purple-100 text-purple-800',
      mediterranean: 'bg-green-100 text-green-800',
      american: 'bg-gray-100 text-gray-800'
    };
    return colors?.[cuisine?.toLowerCase()] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-background/80 backdrop-blur-sm" onClick={onClose} />

        <div className="inline-block w-full max-w-4xl my-8 overflow-hidden text-left align-middle transition-all transform bg-card shadow-warm-lg rounded-lg">
          {/* Header */}
          <div className="relative">
            {dish?.image && (
              <div className="h-64 overflow-hidden">
                <Image
                  src={dish?.image}
                  alt={dish?.imageAlt}
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            <div className="absolute top-4 right-4 flex space-x-2">
              <Button variant="secondary" size="sm" onClick={() => onEdit(dish)}>
                <Icon name="Edit2" size={16} className="mr-2" />
                Edit
              </Button>
              <Button variant="ghost" size="sm" onClick={onClose} className="bg-white/90 backdrop-blur-sm">
                <Icon name="X" size={20} />
              </Button>
            </div>
          </div>

          <div className="p-6">
            {/* Title and Basic Info */}
            <div className="flex items-start justify-between mb-4">
              <div>
                <h1 className="text-2xl font-bold text-foreground mb-2">{dish?.name}</h1>
                <div className="flex items-center space-x-4">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getCuisineColor(dish?.cuisine)}`}>
                    {dish?.cuisine}
                  </span>
                  <div className="flex items-center text-muted-foreground">
                    <Icon name="Clock" size={16} className="mr-1" />
                    <span>{dish?.prepTime} minutes</span>
                  </div>
                  <div className="flex items-center text-muted-foreground">
                    <Icon name="Users" size={16} className="mr-1" />
                    <span>{dish?.servings} servings</span>
                  </div>
                </div>
              </div>

              {/* Rating */}
              {dish?.rating && (
                <div className="flex items-center">
                  <div className="flex items-center mr-2">
                    {[1, 2, 3, 4, 5]?.map((star) => (
                      <Icon
                        key={star}
                        name="Star"
                        size={20}
                        className={star <= dish?.rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}
                      />
                    ))}
                  </div>
                  <span className="text-lg font-medium text-foreground">({dish?.rating}/5)</span>
                </div>
              )}
            </div>

            {/* Dietary Restrictions */}
            {dish?.dietaryRestrictions?.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-medium text-foreground mb-2">Dietary Information</h3>
                <div className="flex flex-wrap gap-2">
                  {dish?.dietaryRestrictions?.map((restriction) => (
                    <div
                      key={restriction}
                      className="flex items-center space-x-2 bg-muted text-muted-foreground px-3 py-1 rounded-full"
                    >
                      <Icon name={getDietaryIcon(restriction)} size={14} className="text-primary" />
                      <span className="text-sm capitalize">{restriction?.replace('-', ' ')}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Ingredients */}
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-3">Ingredients</h3>
                <ul className="space-y-2">
                  {dish?.ingredients?.map((ingredient, index) => (
                    <li key={index} className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-primary rounded-full flex-shrink-0" />
                      <span className="text-foreground">{ingredient}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Usage Statistics */}
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-3">Usage Statistics</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                    <div className="flex items-center space-x-2">
                      <Icon name="BarChart3" size={16} className="text-primary" />
                      <span className="text-sm text-foreground">Times Used</span>
                    </div>
                    <span className="font-medium text-foreground">{dish?.usageCount}</span>
                  </div>

                  {dish?.lastUsed && (
                    <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                      <div className="flex items-center space-x-2">
                        <Icon name="Calendar" size={16} className="text-primary" />
                        <span className="text-sm text-foreground">Last Used</span>
                      </div>
                      <span className="font-medium text-foreground">{formatDate(dish?.lastUsed)}</span>
                    </div>
                  )}

                  <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                    <div className="flex items-center space-x-2">
                      <Icon name="Plus" size={16} className="text-primary" />
                      <span className="text-sm text-foreground">Added</span>
                    </div>
                    <span className="font-medium text-foreground">{formatDate(dish?.createdAt)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Instructions */}
            <div className="mt-8">
              <h3 className="text-lg font-semibold text-foreground mb-3">Cooking Instructions</h3>
              <div className="bg-muted rounded-lg p-4">
                <p className="text-foreground whitespace-pre-line leading-relaxed">{dish?.instructions}</p>
              </div>
            </div>

            {/* Notes */}
            {dish?.notes && (
              <div className="mt-6">
                <h3 className="text-lg font-semibold text-foreground mb-3">Additional Notes</h3>
                <div className="bg-accent/10 border border-accent/20 rounded-lg p-4">
                  <p className="text-foreground whitespace-pre-line leading-relaxed">{dish?.notes}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DishDetailModal;